import { ChevronDown, XCircle } from '@untitled-ui/icons-react';
import React, { ComponentPropsWithoutRef, forwardRef, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { useOutsideClick } from '@/hooks/use-outside-click';
import { cn } from '@/utils/cn';

interface SelectProps extends ComponentPropsWithoutRef<'div'> {
  name?: string;
  leftSide?: React.ReactNode;
  placeholder?: string;
  options?: { label: string; value: string }[];
  onItemSelect?: (value: string) => void;
  value?: string;
  isClearIconHidden?: boolean;
}

const Select = forwardRef<HTMLDivElement, SelectProps>(
  ({ isClearIconHidden, value, options, placeholder, leftSide, className, onItemSelect }, ref) => {
    const { t } = useTranslation();
    const [isDropdownVisible, setIsDropdownVisible] = useState(false);
    const [searchQuery, setSearchQuery] = useState('');
    const dropdownRef = useRef<HTMLDivElement>(null);
    const inputRef = useRef<HTMLInputElement>(null);
    const [isHovered, setIsHovered] = useState(false);

    const selectedItem = options?.find((option) => option.value === value);

    useEffect(() => {
      if (isDropdownVisible && inputRef.current) {
        inputRef.current.focus();
      }
    }, [isDropdownVisible]);

    const handleDropdownClick = () => {
      setIsDropdownVisible((prev) => !prev);
    };

    const handleDropdownItemClick = (value: string) => {
      onItemSelect?.(value);
      setIsDropdownVisible(false);
    };

    const handleSearchChange = (event: React.ChangeEvent<HTMLInputElement>) => {
      setSearchQuery(event.target.value);
    };

    const filteredOptions = options?.filter((option) => option?.label?.toLowerCase()?.includes(searchQuery.toLowerCase()));

    useOutsideClick([dropdownRef], () => {
      setIsDropdownVisible(false);
    });

    const handleClearFilter = () => {
      onItemSelect?.(null);
      setIsDropdownVisible(false);
    };

    return (
      <div ref={dropdownRef} className={cn(className, 'relative')}>
        <div
          onClick={handleDropdownClick}
          className={cn(
            'border h-12 rounded-lg max-w-full group input-component cursor-pointer',
            className,
            isDropdownVisible || value ? 'bg-white border-gray-300' : 'bg-gray-100 border-transparent',
          )}
        >
          <div className="group-hover:bg-gray-200 md:h-12 h-full flex justify-between items-center px-4 gap-2 rounded-lg">
            {leftSide && <span className="grid place-items-center w-6">{leftSide}</span>}

            {isDropdownVisible ? (
              <input
                ref={inputRef}
                type="text"
                placeholder={selectedItem?.label || t('search', { ns: 'header' })}
                value={searchQuery}
                onChange={handleSearchChange}
                className="flex-1 font-normal sm:text-base text-sm text-ellipsis w-0 whitespace-nowrap overflow-hidden bg-transparent border-none focus:outline-none"
              />
            ) : (
              <span
                className={cn(
                  'flex-1 font-normal sm:text-base text-sm text-ellipsis w-0 whitespace-nowrap overflow-hidden',
                  selectedItem ? 'text-gray-10' : 'text-[#B9B9B9]',
                )}
              >
                {selectedItem?.label || placeholder}
              </span>
            )}

            <div onMouseEnter={() => setIsHovered(true)} onMouseLeave={() => setIsHovered(false)}>
              {isHovered && selectedItem?.value && !isClearIconHidden ? (
                <button onClick={handleClearFilter}>
                  <XCircle className="text-[#8C8C8C] w-6 h-6" />
                </button>
              ) : (
                <span className={cn('grid place-items-center', isDropdownVisible ? 'rotate-180' : '')}>
                  <ChevronDown width={24} height={24} color="#8C8C8C" />
                </span>
              )}
            </div>
          </div>
        </div>
        {isDropdownVisible && (
          <div className="shadow-[0px_10px_20px_0px_rgba(0,0,0,0.15)] max-h-[184px] overflow-y-auto py-2 flex flex-col rounded-lg border border-gray-50 top-full mt-1 bg-white z-[1000] left-0 absolute w-full animate-fade-down animate-duration-300">
            {filteredOptions?.length ? (
              filteredOptions.map((option, index) => (
                <div
                  onClick={() => handleDropdownItemClick(option.value)}
                  key={index}
                  className={cn(
                    'py-3 px-4 hover:bg-[#F9FAE0] text-sm cursor-pointer',
                    value === option.value ? 'text-[#A7AD2A]' : 'text-gray-7',
                  )}
                >
                  {option?.label}
                </div>
              ))
            ) : (
              <div className="py-3 text-sm text-gray-7 text-center">{t('noData', { ns: 'table' })}</div>
            )}
          </div>
        )}
      </div>
    );
  },
);

Select.displayName = 'Select';

export default Select;
